<template>
  <div class="component-modal" id="modal">
    <form class="form" v-on:submit.prevent>
      <legend>{{$t("modal.spot_programming.spot.generate_block.title")}}</legend>
      <i class="material-icons icon" @click.stop.prevent="closeModal">close</i>
      
      <div class="component-modal__row">
        <label for="program">{{$t("modal.spot_programming.spot.generate_block.station")}}
          <small v-if="submitted && model.stations.length == 0" class="pill-required">* {{ $t("modal.common.required") }}</small>
          <small v-else>*</small>
        </label>
        <div class="component-modal__row">
          <div class="component-modal-checkbox">
            <label>
              <input type="checkbox" v-model="allStationsActive" @change="allStationsActive ? model.stations = listStations.map(s => Number(s.id)) : model.stations = []">
              {{$t("modal.spot_programming.spot.generate_block.all_station")}} 
            </label>
          </div>
          <template v-for="station in listStations" :key="station.id">
            <div class="component-modal-checkbox">
              <label :for="station.id">
                <input type="checkbox" :name="station.id" :id="station.id" :value="station.id" v-model="model.stations" :disabled="allStationsActive">
                {{ station.name }}
              </label>
            </div>
          </template>
        </div>
    </div>

      <div class="component-modal__row">
        <div class="component-modal-input component-modal-input--rename">
          <input type="date" v-model="model.dateFrom" @change="validate()" :min="dateFrom_minDate" :class="{'invalid': submitted && (v$.model.dateFrom.$invalid || errorTime)}">
          <label for="start">{{$t("modal.spot_programming.spot.generate_block.date_from")}}
            <small v-if="submitted && v$.model.dateFrom.$invalid" class="pill-required">* {{ $t("modal.common.required") }}</small>
            <small v-else>*</small>
          </label>
        </div>
      </div>

      <div class="component-modal__row">
        <div class="component-modal-input component-modal-input--rename">
          <input type="date" v-model="model.dateTo" @change="validate()" :min="model.dateFrom" :max="dateTo_maxDate" :class="{'invalid': submitted && (v$.model.dateTo.$invalid || errorTime)}">
          <label for="end">{{$t("modal.spot_programming.spot.generate_block.date_to")}}
            <small v-if="submitted && v$.model.dateTo.$invalid" class="pill-required">* {{ $t("modal.common.required") }}</small>
            <small v-else>*</small>
          </label>
        </div>
      </div>

      <!-- Alert validation -->
      <div class="alert warning" v-if="errorTime">
        <i class="material-icons">info_outline</i>
        <template v-if="validate_actualDate"> 
          {{ $t("modal.spot_programming.spot.generate_block.validate_actualDate") }}
        </template>
        <template v-else-if="validate_todayDate">
          {{ $t("modal.spot_programming.spot.generate_block.validate_todayDate") }}
        </template>
        <template v-else-if="validate_dateToMaxDays">
          {{ $t("modal.spot_programming.spot.generate_block.validate_dateToMaxDays") }}
        </template>
      </div>
    
      <div class="component-modal__row">
        <button type="button" @click.stop.prevent="saveAndClose">
          {{$t("modal.generate")}}
        </button>
        <button type="button" class="button-cancel" @click.stop.prevent="closeModal">
          {{$t("modal.cancel")}}
        </button>
      </div>

      <div class="component-modal__row" v-if="submitted && v$.model.$invalid">
        <div class="modal__form-error">
          <i class="material-icons">info_outline</i>
          <span v-html="$t('modal.common.required_message', {inputs : 
            [
              model.stations.length == 0 ? $t(`modal.spot_programming.spot.generate_block.station`): '', 
              ...v$.model.$silentErrors.map(error => $t(`modal.spot_programming.spot.generate_block.${error.$property == 'dateFrom' ? 'date_from' : 'date_to'}`)
            )].join(', ') 
          })"></span>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { computed, onMounted, ref } from '@vue/runtime-core';
import { required } from '@vuelidate/validators';
import { useStore } from 'vuex';
import moment from 'moment';
import useVuelidate from '@vuelidate/core';
import { useRoute, useRouter } from 'vue-router';

export default {
  setup() {
		const v$ = useVuelidate();
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const listStations = computed(() => store.getters.station_stations({name: ''}));
    const errorTime = ref(false);
    const submitted = ref(false);
    const allStationsActive = ref(true);
    const dateFrom_minDate = moment().format("YYYY-MM-DD");
    const dateTo_maxDate = computed(() => moment(model.value.dateFrom, 'YYYY-MM-DD').add(45, 'days').format("YYYY-MM-DD"));
    const validate_actualDate = computed(() => moment(model.value.dateFrom).isBefore(moment().format('YYYY-MM-DD')));
    const validate_todayDate = computed(() => moment(model.value.dateTo).isBefore(model.value.dateFrom));
    const validate_dateToMaxDays = computed(() => moment(model.value.dateFrom).add(45, 'days').isBefore(model.value.dateTo)); 
    const model = ref({
      stations: [],
      dateFrom: moment().format("YYYY-MM-DD"),
      dateTo: moment().format("YYYY-MM-DD")
    })

    onMounted(async () => {
      await store.dispatch('station_getAll');
      model.value.stations = listStations.value.map(station => Number(station.id));
    })

    async function closeModal() {
      await store.dispatch('modal_setActive');
      submitted.value = false;
    }

    async function saveAndClose() {
      submitted.value = true;
      validate();
      if (!v$.value.model.$invalid && !errorTime.value && model.value.stations.length > 0) {
        store.dispatch('loading_setCounter', {
          active: true,
          time: (15000 + (moment(model.value.dateTo).diff(moment(model.value.dateFrom), 'days') * 500)) * model.value.stations.length
        });

        try {
          const response = await store.dispatch('trafficLog_generate', model.value);
          const tab = store.getters.tabs_getActive;
          
          response.forEach(async (result) => {
            const tabResult = {
              id: result.requestId,
              module: 'spot_programming',
              name: 'spot_test',
              extendedData: {
                stationName: result.stationName,
                tabActive: tab,
                urlTab: route.path
              }
            }
            await store.dispatch('tabs_addTab', tabResult);
            await store.dispatch('tabs_setActive', tabResult);
          });
          router.push(`/spot_programming/spot_test/${response[0].requestId}`);
          this.closeModal();
        } catch (error) {
          console.error(error);
        }
      }
    }

    function validate() {
      if (model.value.dateFrom != '' && model.value.dateTo != '') {
        // La fecha de inicio tiene que ser mayoy a la fecha de hoy
        if (moment(model.value.dateFrom).isBefore(moment().format("YYYY-MM-DD"))) {
          errorTime.value = true;
          return;
        }

        // La fecha de fin tiene que ser mayor a la fecha de inicio
        if (moment(model.value.dateTo).isBefore(model.value.dateFrom)) {
          model.value.dateTo = model.value.dateFrom;
          return;
        }

        // La fecha de fin no puede ser mayor a 45 dias
        if (moment(model.value.dateFrom).add(45, 'days').isBefore(model.value.dateTo)) {
          errorTime.value = true;
          return;
        }

        // Validar que start sea previo a end
        if (moment(model.value.dateFrom).isAfter(model.value.dateTo)) {
          model.value.dateTo = model.value.dateFrom;
          errorTime.value = false;
        }

        // Si son la misma fecha no validar
        if (moment(model.value.dateFrom).isBefore(moment(model.value.dateTo)) || moment(model.value.dateFrom).isSame(moment(model.value.dateTo))) {
          errorTime.value = false;
          return;
        }
      }
    }

    function updateStations(event) {
      const index = model.value.stations.indexOf(Number(event.target.value));
      if (index === -1){
        model.value.stations.push(Number(event.target.value));
      } else {
        model.value.stations.splice(index, 1);
      }
    }

    return {
      v$,
      dateFrom_minDate,
      dateTo_maxDate,
      validate_actualDate,
      validate_todayDate,
      validate_dateToMaxDays,
      allStationsActive,
      listStations,
      model,
      submitted,
      errorTime,
      saveAndClose,
      closeModal,
      validate,
      updateStations
    }
  },
	validations () {
    return {
      model: {
        dateFrom: {required},
        dateTo: {required}
			}
    }
  }
};
</script>